import { TPreferencesLayer } from '@/shared/features/filter-preferences/context/filter-preferences-config-context.types';
import {
  TFilterDefaults,
  TSavedTableView,
} from '@/shared/types/ui-preferences.types';

// Read fresh, not from cache: a stale or empty snapshot would drop other
// preferences when the backend replaces the blob wholesale, and would hit the
// double-toasting version conflict.
export const commitPreferences = async <TBlob>(
  layer: TPreferencesLayer<TBlob>,
  merge: (blob: TBlob) => TBlob,
): Promise<void> => {
  const fresh = await layer.readFresh();
  await layer.submit({ blob: merge(fresh.blob), version: fresh.version });
};

// '' is a valid stored value (an explicitly cleared default), not a deletion.
export const setFilterDefaultInDefaults = (
  defaults: TFilterDefaults | undefined,
  scopeKey: string,
  param: string,
  value: string,
): TFilterDefaults => ({
  ...defaults,
  [scopeKey]: {
    ...defaults?.[scopeKey],
    filters: {
      ...defaults?.[scopeKey]?.filters,
      [param]: value,
    },
  },
});

// Deletes the key entirely — distinct from storing '' — so resolution falls through.
export const removeFilterDefaultFromDefaults = (
  defaults: TFilterDefaults | undefined,
  scopeKey: string,
  param: string,
): TFilterDefaults => {
  const scope = defaults?.[scopeKey];
  if (!scope?.filters || !(param in scope.filters)) return defaults ?? {};

  const { [param]: _removed, ...filters } = scope.filters;

  return {
    ...defaults,
    [scopeKey]: { ...scope, filters },
  };
};

// Replaces the whole view: a saved view is one coherent set, so a filter dropped
// since the last save must not survive inside it. The per-param defaults beside
// it belong to the filter menu and are left alone.
export const setScopeViewInDefaults = (
  defaults: TFilterDefaults | undefined,
  scopeKey: string,
  view: TSavedTableView,
): TFilterDefaults => ({
  ...defaults,
  [scopeKey]: { ...defaults?.[scopeKey], view },
});

// Drops the scope only once the view was the last thing in it, so an empty
// husk never keeps the scope looking populated.
const withoutView = (
  defaults: TFilterDefaults,
  scopeKey: string,
): TFilterDefaults => {
  const { view: _removed, ...rest } = defaults[scopeKey];

  if (Object.keys(rest).length === 0) {
    const { [scopeKey]: _scope, ...others } = defaults;
    return others;
  }

  return { ...defaults, [scopeKey]: rest };
};

export const removeScopeViewFromDefaults = (
  defaults: TFilterDefaults | undefined,
  scopeKey: string,
): TFilterDefaults => {
  if (!defaults?.[scopeKey]?.view) return defaults ?? {};

  return withoutView(defaults, scopeKey);
};

export const removeAllScopeViewsFromDefaults = (
  defaults: TFilterDefaults | undefined,
): TFilterDefaults =>
  Object.keys(defaults ?? {}).reduce<TFilterDefaults>(
    (acc, scopeKey) => withoutView(acc, scopeKey),
    { ...defaults },
  );
